import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Stack,
  Table,
  Button,
  Divider,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';

// hooks
import useTabs from '../../../hooks/useTabs';
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator, emptyRows } from '../../../hooks/useTable';
// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { TableNoData, TableEmptyRows, TableHeadCustom } from '../../../components/table';
// column visibility
import useColumnVisibility, { ColumnDef } from 'src/hooks/useColumnVisibility';
import { ColumnsButton } from 'src/components/table';
// sections
import InstructorTableRow from './InstructorsTableRow';
import { InstructorsModal } from './InstructorsModal';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { InstructorType } from './types';
import InstructorTableToolbar from './InstructorsTableToolbar';
import Question from './Test';
import LoadingScreen from 'src/components/LoadingScreen';
import ConfirmationDialog from './ConformiationDialog';
import { useSnackbar } from 'notistack';

// ----------------------------------------------------------------------

const Sort_Options = ['all', 'acsending', 'descending'];

const INSTRUCTORS_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Name', align: 'left' },
  { id: 'email', label: 'Email', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'createdAt', label: 'No. of Courses', align: 'left' },
  { id: 'diplomaBatches', label: 'Diploma batches', align: 'left' },
  { id: 'active', label: 'Active', align: 'left' },
];

// ----------------------------------------------------------------------

export default function InstructorsList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState<InstructorType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [filterName, setFilterName] = useState('');
  // instructor id -> ["Diploma · Batch", ...] (which batches they lead).
  const [instructorBatches, setInstructorBatches] = useState<Record<string, string[]>>({});
  // instructor id -> real course count (counted by the course→instructor ref).
  const [courseCounts, setCourseCounts] = useState<Record<string, number>>({});

  const [filterRole, setFilterRole] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState({ open: false, currentId: '' });
  const [currentInstructor, setCurrentInstructor] = useState<InstructorType | undefined>();

  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs('all');

  const { visibleColumns, toggleColumn, headLabel } = useColumnVisibility(
    INSTRUCTORS_COLUMNS,
    'kayfa.instructors.visibleColumns'
  );

  const getInstructorsList = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(APIS.INSTRUCTORS.LIST);
      console.log('instructors data', res.data);
      setLoading(false);
      setTableData(res.data.data);
    } catch (error) {
      setLoading(false);
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to get instructors!', { variant: 'error' });
    }
  };

  useEffect(() => {
    getInstructorsList();
  }, []);

  // Map each instructor to the diploma batches they lead (from active diplomas).
  useEffect(() => {
    axiosInstance
      .get(APIS.DIPLOMAS.ACTIVE)
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const map: Record<string, string[]> = {};
        (Array.isArray(list) ? list : []).forEach((d: any) => {
          (d.batches || []).forEach((b: any) => {
            const insId =
              typeof b.instructor === 'object' ? b.instructor?._id : b.instructor;
            if (insId) {
              if (!map[insId]) map[insId] = [];
              map[insId].push(`${d.title} · ${b.name}`);
            }
          });
        });
        setInstructorBatches(map);
      })
      .catch(() => setInstructorBatches({}));
  }, []);

  // Count courses per instructor by the course→instructor reference (the
  // instructor entity's own `courses` array is empty for many records).
  useEffect(() => {
    axiosInstance
      .get(APIS.COURSES.LIST)
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        const arr = Array.isArray(list) ? list : list?.items ?? [];
        const map: Record<string, number> = {};
        arr.forEach((c: any) => {
          const insId = c?.instructor?._id || c?.instructor;
          if (insId) map[String(insId)] = (map[String(insId)] || 0) + 1;
        });
        setCourseCounts(map);
      })
      .catch(() => setCourseCounts({}));
  }, []);

  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
  };

  const handleFilterRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterRole(event.target.value);
  };

  const handleDeleteRow = async (id: string) => {
    try {
      setLoading(true);
      await axiosInstance.delete(APIS.INSTRUCTORS.PATCH(id));
      const deleteRow = tableData?.filter((row) => row._id !== id);
      setSelected([]);
      setLoading(false);
      setTableData(deleteRow);
    } catch (error) {
      setLoading(false);
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    }
  };

  const handleEditRow = (instructor: InstructorType) => {
    setCurrentInstructor(instructor);
    setModalOpen(true);
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterRole,
    filterStatus,
  });

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && !!filterRole) ||
    (!dataFiltered.length && !!filterStatus);

  return (
    <Page title="Instructors: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Instructors List"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Instructors List' }]}
          action={
            <Button
              variant="contained"
              onClick={() => setModalOpen(true)}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New Instructor
            </Button>
          }
        />

        <Card>
          <Divider />
          <InstructorTableToolbar
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
            optionsRole={Sort_Options}
          />

          <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pb: 2 }}>
            <ColumnsButton
              columns={INSTRUCTORS_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {loading ? (
                <LoadingScreen />
              ) : (
                <Table size={dense ? 'small' : 'medium'}>
                  <TableHeadCustom
                    order={order}
                    orderBy={orderBy}
                    headLabel={[...headLabel, { id: '' }]}
                    rowCount={tableData?.length}
                    onSort={onSort}
                  />

                  <TableBody>
                    {dataFiltered.length > 0 &&
                      dataFiltered
                        ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        ?.map((row) => (
                          <InstructorTableRow
                            key={row._id}
                            row={row}
                            visibleColumns={visibleColumns}
                            diplomaBatches={instructorBatches[row._id] || []}
                            courseCount={courseCounts[row._id] || 0}
                            selected={selected.includes(row._id)}
                            onDeleteRow={() => setConfirmOpen({ open: true, currentId: row._id })}
                            onEditRow={() => handleEditRow(row)}
                            onViewRow={() => navigate(PATH_DASHBOARD.instructorDetail(row._id))}
                          />
                        ))}

                    <TableEmptyRows
                      height={2.2}
                      emptyRows={emptyRows(page, rowsPerPage, tableData?.length)}
                    />

                    <Question />

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <InstructorsModal
        isEdit={currentInstructor ? true : false}
        currentInstructor={currentInstructor}
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          setCurrentInstructor(undefined);
        }}
        getInstructorsList={getInstructorsList}
      />

      <ConfirmationDialog
        isOpen={confirmOpen.open}
        onClose={() => setConfirmOpen({ open: false, currentId: '' })}
        confirmAction={() => handleDeleteRow(confirmOpen.currentId)}
        loading={loading}
      />
    </Page>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
  filterStatus,
  filterRole,
}: {
  tableData: InstructorType[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterStatus: string;
  filterRole: string;
}) {
  const stabilizedThis = tableData?.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    // Search across ANY displayed field (name, email, phone, specialization,
    // bio, etc.) — not just the name — so the toolbar search matches anything
    // in the row (parity with the Students tab). Skips ids/urls/timestamps.
    const q = filterName.toLowerCase().trim();
    const SKIP = new Set([
      '_id',
      'id',
      'avatar',
      'image',
      'password',
      '__v',
      'createdAt',
      'updatedAt',
    ]);
    tableData = tableData?.filter((item: Record<string, any>) =>
      Object.entries(item || {}).some(
        ([k, v]) =>
          !SKIP.has(k) &&
          (typeof v === 'string' || typeof v === 'number') &&
          String(v).toLowerCase().includes(q)
      )
    );
  }

  if (filterStatus !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.status === filterStatus);
  }

  if (filterRole !== 'all') {
    tableData = tableData?.filter((item: Record<string, any>) => item.role === filterRole);
  }

  return tableData;
}
